import React, { useState } from 'react';
import axios from 'axios';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import CancelButton from '~/components/core/Buttons/CancelButton';
import Text from '~/components/core/TextComponents/Text';
import { PencilIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import LoadingIndicator from '~/components/LoadingIndicator';
import TextFieldFormik from '~/components/TextFieldFormik';
import Switch from '~/components/ui/atoms/Switch';
import useDataFetcher from '~/components/useDataFetcher';

import { reportAxiosError } from '../../../Utils';
import { useSysconfig } from '../SystemConfigurationScreen';

interface SessionTimeoutMinutesFormValues {
  session_timeout_minutes: number | null;
}

interface LoginConfigurationData {
  session_timeout_minutes?: number | null;
  [key: string]: unknown;
}

const header = (
  <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
    Session Timeout
  </Text>
);

const SessionTimeoutConfiguration: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { classes } = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLoading, isError, data, reloadData } = useDataFetcher<LoginConfigurationData>(
    `/sysconfig/api/v1/organizations/${organization.id}/login_configurations`
  );

  if (isLoading || isError) {
    return (
      <>
        {header}
        <LoadingIndicator isError={isError} />
      </>
    );
  }

  const handleSwitchChange = (enabled: boolean) => {
    if (enabled) {
      setIsModalOpen(true);
    } else {
      handleDisableSessionTimeout();
    }
  };

  const handleDisableSessionTimeout = async () => {
    await updateSessionTimeout(null);
  };

  const handleModalSubmit = async (values: SessionTimeoutMinutesFormValues) => {
    const success = await updateSessionTimeout(values.session_timeout_minutes);
    if (success) {
      setIsModalOpen(false);
    }
  };

  const updateSessionTimeout = async (minutes: number | null): Promise<boolean> => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/login_configurations/session_timeout`, {
        session_timeout_minutes: minutes,
      });
      await reloadData();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    }
  };

  const timeoutMinutes = data?.session_timeout_minutes;
  const isEnabled = !!timeoutMinutes && timeoutMinutes > 0;

  return (
    <div className="mt-40">
      {header}
      <Grid container spacing={1} className="mt-2">
        <Grid item xs={12}>
          <label>
            <Switch checked={isEnabled} onChange={(event, checked) => handleSwitchChange(checked)} disabled={false} />
            Enabled
          </label>
        </Grid>
        {isEnabled && (
          <Grid item xs={12} container className="mt-10">
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
              Session timeout is set to {timeoutMinutes} minutes
            </Text>
            <InlineIconButton
              icon={PencilIcon}
              onClick={() => setIsModalOpen(true)}
              className={classes.hoverableNonFilledIcon}
              tooltipTitle="Edit session timeout"
            />
          </Grid>
        )}
      </Grid>
      {isModalOpen && (
        <SessionTimeoutModal
          initialMinutes={timeoutMinutes ?? 10}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
        />
      )}
    </div>
  );
};

interface SessionTimeoutModalProps {
  initialMinutes: number | null;
  onClose: () => void;
  onSubmit: (
    values: SessionTimeoutMinutesFormValues,
    formikHelpers: FormikHelpers<SessionTimeoutMinutesFormValues>
  ) => Promise<void>;
}

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({ initialMinutes, onClose, onSubmit }) => {
  const { classes } = useStyles();

  return (
    <Formik<SessionTimeoutMinutesFormValues>
      initialValues={{
        session_timeout_minutes: initialMinutes,
      }}
      validationSchema={Yup.object().shape({
        session_timeout_minutes: Yup.number()
          .nullable()
          .required('Session timeout minutes is required')
          .min(10, 'Session timeout must be at least 10 minutes')
          .max(3 * 30 * 24 * 60, 'Session timeout must be at most 3 months (129,600 minutes)')
          .integer('Session timeout must be a whole number'),
      })}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => {
        return (
          <CardDialog isDialog title="Session Timeout Configuration" onClose={onClose}>
            <Grid container component={Form} spacing={1}>
              <Grid item xs={12}>
                <TextFieldFormik
                  className={classes.textField}
                  id="session_timeout_minutes"
                  label="Enter session timeout duration in minutes:"
                  // @ts-expect-error - TextFieldFormik types don't properly support type prop
                  type="number"
                  // @ts-expect-error - TextFieldFormik types don't properly support inputMode prop
                  inputMode="numeric"
                  fullWidth
                />
              </Grid>
              <Grid container className="pt-8">
                <div className={classes.buttonsContainer}>
                  <CancelButton disabled={isSubmitting} onClick={onClose} contained />
                  <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                    Save
                  </Button>
                </div>
              </Grid>
            </Grid>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

export default SessionTimeoutConfiguration;
